import { NavLink } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  useLatestScan,
  useRunScan,
  useVitalsSummary,
  type SeoScan,
  type VitalsSummary,
} from '@/hooks/useSeoData';
import { cn } from '@/lib/utils/cn';
import { IconRefresh } from '@/components/home/icons';

/**
 * /admin/seo — Technical SEO dashboard.
 *
 * Live data sources (implemented):
 *   #1 Sitemap + Robots         → /api/seo/scan (Cloudflare Pages Function)
 *   #3 Total Pages + Broken     → same endpoint (in-house sitemap crawler)
 *   #6 Core Web Vitals          → /api/seo/vitals (real-user beacon stream)
 *
 * Placeholder widgets (awaiting external API setup):
 *   - Crawl Frequency by Bot, Mobile/Desktop Traffic   → Cloudflare Analytics
 *   - Backlinks, Avg Keyword Position, Anomaly Triggers → Google Search Console
 *   - PA Score                                          → PageSpeed Insights
 */
export function SeoDashboard() {
  useDocumentTitle('Technical SEO · Admin');

  const scan = useLatestScan();
  const vitals = useVitalsSummary();
  const runScan = useRunScan();

  const latest = scan.data ?? null;
  const isRunning = runScan.isPending;

  return (
    <div className="flex min-h-screen bg-night-900 text-night-50">
      <AdminSidebar />

      <div className="flex-1 lg:ml-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-night-500/10 bg-night-900/80 px-6 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <h2 className="font-display text-xl font-bold text-beam">Technical SEO</h2>
            <span className="rounded border border-beam/20 bg-beam/10 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.18em] text-beam">
              Live Command
            </span>
          </div>
          <button
            type="button"
            onClick={() => runScan.mutate()}
            disabled={isRunning}
            className="inline-flex items-center gap-2 rounded-lg border border-beam/40 bg-beam/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-beam transition-colors hover:bg-beam/20 disabled:opacity-50"
          >
            <IconRefresh
              className={cn('h-4 w-4', isRunning && 'animate-spin')}
            />
            {isRunning ? 'Scanning…' : 'Refresh'}
          </button>
        </header>

        <main className="custom-scrollbar p-6">
          {runScan.isError && (
            <div className="mb-4 rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
              Scan failed: {(runScan.error as Error).message}
            </div>
          )}

          <div className="grid grid-cols-12 gap-6">
            {/* Left column — KPIs */}
            <div className="col-span-12 space-y-6 lg:col-span-3">
              <SitemapCard scan={latest} loading={scan.isLoading} />
              <BacklinksCard />
              <BrokenLinksCard scan={latest} loading={scan.isLoading} />
              <DaScoreCard />
            </div>

            {/* Center — prominent charts */}
            <div className="col-span-12 space-y-6 lg:col-span-6">
              <CrawlFrequencyCard />
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <ClsCard vitals={vitals.data ?? null} loading={vitals.isLoading} />
                <FidCard vitals={vitals.data ?? null} loading={vitals.isLoading} />
              </div>
              <LcpCard vitals={vitals.data ?? null} loading={vitals.isLoading} />
            </div>

            {/* Right column — anomalies + traffic */}
            <div className="col-span-12 space-y-6 lg:col-span-3">
              <TotalPagesCard scan={latest} loading={scan.isLoading} />
              <AnomalyTriggerCard scan={latest} />
              <KeywordPositionCard />
            </div>

            {/* Footer KPIs */}
            <div className="col-span-12 grid grid-cols-2 gap-6 md:grid-cols-4">
              <RobotsCard scan={latest} loading={scan.isLoading} />
              <TrafficCard
                label="Mobile Traffic"
                value={vitals.data?.traffic.mobile_pct ?? null}
                loading={vitals.isLoading}
              />
              <TrafficCard
                label="Desktop Traffic"
                value={vitals.data?.traffic.desktop_pct ?? null}
                loading={vitals.isLoading}
              />
              <ScanFreshnessCard scan={latest} />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

/* ============================================================ */
/* Layout                                                       */
/* ============================================================ */

function AdminSidebar() {
  const items: { to: string; label: string; active?: boolean }[] = [
    { to: '/admin', label: 'Overview' },
    { to: '/admin/seo', label: 'Technical SEO', active: true },
    { to: '/admin/backup', label: 'Backup & Restore' },
    { to: '/admin/qari', label: 'Qari' },
    { to: '/admin/students', label: 'Students' },
    { to: '/admin/cms', label: 'CMS' },
  ];
  return (
    <aside className="fixed left-0 top-0 z-40 hidden h-screen w-64 flex-col border-r border-night-500/20 bg-night-950 px-2 py-6 lg:flex">
      <div className="mb-10 px-4">
        <h1 className="font-display text-2xl font-black tracking-tight text-night-50">
          Alhudah Admin
        </h1>
        <p className="mt-0.5 text-[10px] uppercase tracking-[0.18em] text-night-300">
          Quran Education Platform
        </p>
      </div>
      <nav className="flex-1 space-y-1">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-r-lg px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em] transition-colors',
                (isActive || item.active)
                  ? 'border-r-2 border-beam bg-beam/10 text-beam'
                  : 'text-night-200 hover:bg-night-700/30 hover:text-night-50',
              )
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="mt-auto px-2">
        <div className="flex items-center justify-center gap-2 rounded-lg border border-beam/20 bg-night-800 py-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-beam">
          System Secure
        </div>
      </div>
    </aside>
  );
}

/* ============================================================ */
/* Card primitives                                              */
/* ============================================================ */

function Card({
  children,
  className,
  accent,
}: {
  children: React.ReactNode;
  className?: string;
  accent?: 'rose' | 'beam';
}) {
  return (
    <div
      className={cn(
        'rounded-xl border border-night-500/30 bg-night-800/40 p-6 backdrop-blur-md transition-all hover:border-beam/60',
        accent === 'rose' && 'border-l-4 border-l-rose-500',
        accent === 'beam' && 'border-l-4 border-l-beam',
        className,
      )}
    >
      {children}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-night-200">
      {children}
    </p>
  );
}

function Placeholder({ note }: { note: string }) {
  return (
    <span className="rounded bg-night-500/20 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.18em] text-night-300">
      {note}
    </span>
  );
}

/* ============================================================ */
/* Live widgets — wired to the three implemented sources         */
/* ============================================================ */

function SitemapCard({ scan, loading }: { scan: SeoScan | null; loading: boolean }) {
  const status = scan?.sitemap_status;
  const tone =
    status === 'valid'
      ? 'text-beam'
      : status === 'invalid'
        ? 'text-amber-300'
        : 'text-rose-300';
  return (
    <Card>
      <div className="mb-4 flex items-start justify-between">
        <div>
          <Label>Sitemap Status</Label>
          <h3 className={cn('mt-1 text-2xl font-bold', tone)}>
            {loading ? '…' : status ? statusLabel(status) : 'Not scanned'}
          </h3>
        </div>
      </div>
      <p className="text-[10px] uppercase tracking-wider text-night-300">
        {scan ? `${scan.sitemap_url_count} URLs · ${relativeTime(scan.ran_at)}` : 'Run a scan to populate'}
      </p>
    </Card>
  );
}

function BrokenLinksCard({ scan, loading }: { scan: SeoScan | null; loading: boolean }) {
  const broken = scan?.pages_broken ?? null;
  const hasBroken = broken !== null && broken > 0;
  return (
    <Card accent={hasBroken ? 'rose' : undefined}>
      <div className="mb-4 flex items-start justify-between">
        <div>
          <Label>Broken Links</Label>
          <h3
            className={cn(
              'mt-1 text-2xl font-bold',
              hasBroken ? 'text-rose-300' : 'text-night-50',
            )}
          >
            {loading ? '…' : broken !== null ? broken : '—'}
          </h3>
        </div>
      </div>
      <p
        className={cn(
          'text-[10px] uppercase tracking-wider',
          hasBroken ? 'text-rose-300' : 'text-night-300',
        )}
      >
        {hasBroken ? 'Fix Required' : 'All clean'}
      </p>
    </Card>
  );
}

function TotalPagesCard({ scan, loading }: { scan: SeoScan | null; loading: boolean }) {
  return (
    <Card>
      <Label>Total Pages</Label>
      <h3 className="mt-2 font-display text-3xl font-bold text-night-50">
        {loading ? '…' : scan?.pages_total ?? '—'}
      </h3>
      <p className="mt-2 text-[10px] uppercase tracking-wider text-night-300">
        {scan ? `${scan.pages_ok} OK · ${scan.pages_broken} broken` : 'Run a scan'}
      </p>
    </Card>
  );
}

function RobotsCard({ scan, loading }: { scan: SeoScan | null; loading: boolean }) {
  const status = scan?.robots_status;
  return (
    <Card>
      <Label>Robots.txt</Label>
      <h3
        className={cn(
          'mt-2 text-lg font-bold',
          status === 'optimized' ? 'text-beam' : 'text-night-50',
        )}
      >
        {loading ? '…' : status ? statusLabel(status) : 'Not scanned'}
      </h3>
      <p className="mt-1 text-[10px] uppercase tracking-wider text-night-300">
        {status === 'optimized'
          ? 'Sitemap + Disallow present'
          : status
            ? 'Needs review'
            : ''}
      </p>
    </Card>
  );
}

function AnomalyTriggerCard({ scan }: { scan: SeoScan | null }) {
  const broken = scan?.broken_sample ?? [];
  return (
    <Card accent="rose">
      <div className="mb-4 flex items-center justify-between">
        <Label>Anomaly Trigger</Label>
        <span className="rounded bg-rose-500/15 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.18em] text-rose-300">
          Caution
        </span>
      </div>
      {broken.length === 0 ? (
        <p className="text-sm text-night-300">No anomalies detected.</p>
      ) : (
        <ul className="space-y-2">
          {broken.slice(0, 4).map((b, i) => (
            <li
              key={`${b.url}-${i}`}
              className="flex items-center justify-between gap-3 text-xs"
            >
              <span className="truncate text-night-100">{b.url}</span>
              <span className="rounded bg-rose-500/15 px-1.5 py-0.5 font-semibold text-rose-200">
                {b.status || 'ERR'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ClsCard({ vitals, loading }: { vitals: VitalsSummary | null; loading: boolean }) {
  const m = vitals?.metrics?.CLS;
  return (
    <Card>
      <Label>CLS (Layout Shift)</Label>
      <h3 className="mt-2 font-display text-3xl font-bold text-night-50">
        {loading ? '…' : m ? m.median.toFixed(3) : '—'}
      </h3>
      <p className="mt-1 text-[10px] uppercase tracking-wider text-night-300">
        {m ? `${m.count} samples` : 'Awaiting beacons'}
      </p>
    </Card>
  );
}

function FidCard({ vitals, loading }: { vitals: VitalsSummary | null; loading: boolean }) {
  // Modern web-vitals replaces FID with INP. We surface INP under the
  // "FID" tile so the design's intent (input responsiveness) stays intact.
  const m = vitals?.metrics?.INP;
  return (
    <Card>
      <Label>INP (Input Delay)</Label>
      <h3 className="mt-2 font-display text-3xl font-bold text-night-50">
        {loading ? '…' : m ? `${Math.round(m.median)}ms` : '—'}
      </h3>
      <p className="mt-1 text-[10px] uppercase tracking-wider text-night-300">
        {m ? `${m.count} samples` : 'Awaiting beacons'}
      </p>
    </Card>
  );
}

function LcpCard({ vitals, loading }: { vitals: VitalsSummary | null; loading: boolean }) {
  const m = vitals?.metrics?.LCP;
  return (
    <Card>
      <div className="flex items-center justify-between">
        <Label>LCP (Largest Contentful Paint)</Label>
        {m && (
          <span className="text-xs text-night-300">{m.count} samples</span>
        )}
      </div>
      <h3 className="mt-2 font-display text-3xl font-bold text-night-50">
        {loading ? '…' : m ? `${(m.median / 1000).toFixed(2)}s` : '—'}
      </h3>
      <p className="mt-1 text-[10px] uppercase tracking-wider text-night-300">
        Real-user median, last 7 days
      </p>
    </Card>
  );
}

function TrafficCard({
  label,
  value,
  loading,
}: {
  label: string;
  value: number | null;
  loading: boolean;
}) {
  return (
    <Card>
      <Label>{label}</Label>
      <h3 className="mt-2 font-display text-3xl font-bold text-night-50">
        {loading ? '…' : value !== null ? `${value}%` : '—'}
      </h3>
      <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-night-700">
        <div
          className="h-full rounded-full bg-beam shadow-[0_0_8px_rgba(0,229,255,0.5)]"
          style={{ width: `${value ?? 0}%` }}
        />
      </div>
    </Card>
  );
}

function ScanFreshnessCard({ scan }: { scan: SeoScan | null }) {
  return (
    <Card>
      <Label>Last Scan</Label>
      <h3 className="mt-2 text-lg font-bold text-night-50">
        {scan ? relativeTime(scan.ran_at) : 'Never'}
      </h3>
      <p className="mt-1 text-[10px] uppercase tracking-wider text-night-300">
        {scan ? `${scan.duration_ms}ms · ${scan.source}` : 'Hit Refresh to scan'}
      </p>
    </Card>
  );
}

/* ============================================================ */
/* Placeholder widgets — need external API integrations          */
/* ============================================================ */

function BacklinksCard() {
  return (
    <Card>
      <div className="mb-2 flex items-start justify-between">
        <Label>Backlinks</Label>
        <Placeholder note="GSC" />
      </div>
      <h3 className="mt-1 text-2xl font-bold text-night-300">—</h3>
      <p className="mt-1 text-[10px] uppercase tracking-wider text-night-300">
        Connect Google Search Console
      </p>
    </Card>
  );
}

function DaScoreCard() {
  return (
    <Card>
      <div className="mb-2 flex items-start justify-between">
        <Label>DA Score</Label>
        <Placeholder note="PSI" />
      </div>
      <h3 className="mt-1 text-2xl font-bold text-night-300">—/100</h3>
      <p className="mt-1 text-[10px] uppercase tracking-wider text-night-300">
        Connect PageSpeed Insights
      </p>
    </Card>
  );
}

function CrawlFrequencyCard() {
  // Static visual placeholder — same bar layout as the design, neutral data.
  const bots = [
    { name: 'Google', height: 90 },
    { name: 'Bing', height: 60 },
    { name: 'Bardot', height: 35 },
    { name: 'Bingbot', height: 75 },
    { name: 'Yandex', height: 45 },
    { name: 'Baidu', height: 25 },
  ];
  return (
    <Card>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <Label>Crawl Frequency by Bot</Label>
          <p className="mt-1 text-xs text-night-300">
            Awaiting Cloudflare Analytics integration
          </p>
        </div>
        <Placeholder note="CF Analytics" />
      </div>
      <div className="flex h-48 items-end justify-between gap-3">
        {bots.map((bot) => (
          <div key={bot.name} className="flex flex-1 flex-col items-center gap-2">
            <div
              className="w-full rounded-t-md bg-beam/30"
              style={{ height: `${bot.height}%` }}
            />
            <span className="text-[10px] uppercase tracking-wider text-night-300">
              {bot.name}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function KeywordPositionCard() {
  return (
    <Card>
      <div className="mb-2 flex items-start justify-between">
        <Label>Avg Keyword Pos.</Label>
        <Placeholder note="GSC" />
      </div>
      <h3 className="mt-1 font-display text-3xl font-bold text-night-300">#—</h3>
      <p className="mt-1 text-[10px] uppercase tracking-wider text-night-300">
        Connect Google Search Console
      </p>
    </Card>
  );
}

/* ============================================================ */
/* Helpers                                                      */
/* ============================================================ */

function statusLabel(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}
